import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthContext';
import Breadcrumb from '@/shared/components/Breadcrumb';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

/**
 * Estimations d'un agrégateur AJOUTÉ À LA MAIN — le gabarit de Survoltage, sur sa fiche.
 *
 * Revue du 31/08 : « tilt est créé par David mais non ouvrable. On doit pouvoir ouvrir
 * et modifier les calculs comme tu le vois pour Symphonics et Survoltage. Une fois la
 * page nouvel agrégateur ajouté ouverte, il va falloir que la page aie le même gabarit
 * que celle de survoltage. »
 *
 * CE QUE CETTE PAGE PARTAGE AVEC SURVOLTAGE, ET CE QU'ELLE NE PARTAGE PAS
 *
 * Elle en reprend la STRUCTURE — fil d'Ariane, titre, badge de rôle, bandeau
 * d'avertissement, tuiles de montants, bloc de réglages, bouton Enregistrer — parce
 * qu'un admin qui passe de l'un à l'autre doit retrouver ses repères.
 *
 * Elle n'en reprend PAS le barème. Survoltage calcule par profil de client (forfaits
 * juillet 2026), Symphonics par mécanisme de capacité : les deux ne se ramènent pas
 * l'un à l'autre, et c'est précisément pourquoi ils ont chacun leur écran codé à la
 * main. Ici le barème est par type d'actif et par mécanisme, la forme que le moteur
 * commun `services/revenus` attend déjà.
 *
 * ⚠️ TOUS LES MONTANTS SONT DES ESTIMATIONS, et les intitulés le disent — même règle
 * que les cinquante et un montants du lot N. Le moteur rend un revenu théorique, celui
 * d'un parc entièrement joignable ; l'état du parc et les décotes disent ce qu'on en
 * attend vraiment.
 */

const MECANISMES_LIBELLES = {
  meca_capa: 'Capacitaire (MECAPA)',
  nebco: 'Énergie (NEBCO)',
  marche_ajustement: "Mécanisme d'ajustement",
  r2: 'Réserve secondaire (R2)',
};

const euros = (v, d = 0) => `${Number(v || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: d, maximumFractionDigits: d })} €`;

const pourcent = (v) => `${Math.round(Number(v || 0) * 100)} %`;

export default function AgregateurRevenusPage() {
  const { cle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);
  const [reglages, setReglages] = useState({ etat_parc: 1, part_operateur: 0.3 });

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const { data } = await api.get(`/aggregateurs/${cle}/revenus`);
      setDonnees(data);
      setReglages({
        etat_parc: data.configuration.parametres_calcul?.etat_parc ?? 1,
        part_operateur: data.configuration.parametres_calcul?.part_operateur ?? 0.3,
      });
    } catch (e) {
      // Un 409 n'est PAS une erreur : c'est un agrégateur intégré, qui a son propre
      // écran. On y va, plutôt que d'afficher un rouge sur un cas normal.
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 409 && detail.includes('/')) {
        const route = detail.match(/(\/[a-z-]+)\./);
        if (route) { navigate(route[1]); return; }
      }
      setErreur(detail || "Les estimations de cet agrégateur n'ont pas pu être chargées.");
    }
    setChargement(false);
  }, [cle, navigate]);

  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      await api.put(`/aggregateurs/${donnees.agregateur.key}`, {
        parametres_calcul: {
          etat_parc: Number(reglages.etat_parc),
          part_operateur: Number(reglages.part_operateur),
        },
      });
      toast.success('Réglages enregistrés');
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Les réglages n'ont pas pu être enregistrés");
    }
    setEnregistrement(false);
  };

  if (chargement) {
    return (
      <div className="p-10 flex items-center gap-2 text-neutral-500" data-testid="agg-revenus-chargement">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }
  if (erreur) {
    return (
      <div className="p-6 md:p-8 lg:p-10" data-testid="agg-revenus-erreur">
        <Breadcrumb items={[{ label: 'Agrégateurs', to: '/agregateurs' }, { label: 'Estimations' }]} />
        <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-100 text-[13px] text-red-700">
          {erreur}
        </div>
      </div>
    );
  }

  const { agregateur: agg, totaux, par_type: parType, par_mecanisme: parMecanisme,
          exclusions_par_motif: exclusions, configuration: cfg } = donnees;
  const aucunBareme = Object.keys(cfg.remuneration_schedule || {}).length === 0;

  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-6" data-testid="agg-revenus-page">
      <div>
        <Breadcrumb items={[{ label: 'Agrégateurs', to: '/agregateurs' },
                            { label: `Estimations ${agg.name}` }]} />
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
            Estimations {agg.name}
          </h1>
          <Badge variant="outline" className="bg-neutral-50 text-neutral-700 border-neutral-200 text-[10px]">
            Opérateur / Admin
          </Badge>
          {!donnees.pilotage_disponible && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                   data-testid="agg-sans-pilotage">
              Rémunération seule — aucun pilotage
            </Badge>
          )}
        </div>
        <p className="text-sm text-neutral-400 mt-0.5">
          {totaux.actifs_pris_en_compte} actif{totaux.actifs_pris_en_compte > 1 ? 's' : ''} pris
          en compte · {totaux.actifs_exclus} écarté{totaux.actifs_exclus > 1 ? 's' : ''}
          {agg.tagline ? ` · ${agg.tagline}` : ''}
        </p>
        {isAdmin && (
          <div className="mt-2">
            <Button size="sm" onClick={enregistrer} disabled={enregistrement} data-testid="agg-revenus-save">
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        )}
      </div>

      {/* Le bandeau qui dit ce que l'écran NE dit PAS. Même rôle que celui de
          Survoltage : un montant sans son hypothèse se lit comme une promesse. */}
      {aucunBareme ? (
        <div className="p-3 rounded-md bg-amber-50/70 border border-amber-200 text-[12px] text-amber-900 flex items-start gap-2"
             data-testid="agg-sans-bareme">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Aucun barème n&apos;est renseigné pour {agg.name}.</strong>{' '}
            Tous les montants ci-dessous valent donc zéro, et ce zéro ne décrit pas le
            parc : il décrit un barème vide. Renseignez-le depuis l&apos;écran
            Agrégateurs, bouton « Modifier ».
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-md bg-neutral-50 border border-neutral-200 text-[12px] text-neutral-700 flex items-start gap-2"
             data-testid="agg-hypotheses">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-neutral-400" />
          <div>
            <strong>Ces montants sont des estimations.</strong>{' '}
            Le calcul part du barème saisi, puis applique l&apos;état du parc
            ({pourcent(totaux.etat_parc)}){Object.keys(totaux.decotes_appliquees || {}).length > 0
              && ` et ${Object.keys(totaux.decotes_appliquees).length} décote(s)`}, soit un
            facteur de {pourcent(totaux.facteur_applique)}. Un actif sans mandat, déconnecté
            ou dont le type n&apos;est pas au barème est écarté et compté à part.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {/* L'identifiant de chaque tuile est EXPLICITE. Il etait derive du libelle
            (`libelle.slice(0, 12)`) : renommer le libelle renommait la tuile sans
            un mot, et toute recette accrochee dessus tombait a la execution
            suivante. Un nom d'affichage n'est jamais une cle. */}
        {[
          ['brut-annuel', 'Estimation annuelle brute', totaux.brut_annuel_estime, 'text-neutral-900'],
          ['quotepart-annuelle', 'Quote-part opérateur annuelle estimée', totaux.quotepart_annuelle_estimee, 'text-emerald-700'],
          ['quotepart-mensuelle', 'Quote-part mensuelle estimée', totaux.quotepart_mensuelle_estimee, 'text-emerald-700'],
          ['part-agregateur', 'Part agrégateur estimée', totaux.part_agregateurs, 'text-neutral-500'],
        ].map(([id, libelle, valeur, couleur]) => (
          <div key={id} className="rounded-xl border border-neutral-200 bg-white p-4"
               data-testid={`agg-tuile-${id}`}>
            <p className="text-[10.5px] uppercase tracking-wide text-neutral-400">{libelle}</p>
            <p className={`text-[26px] font-semibold mt-1 ${couleur}`}>{euros(valeur)}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <p className="text-[12px] font-medium text-neutral-700 mb-3">
            Estimation brute par type d&apos;actif
          </p>
          {Object.keys(parType).length === 0
            ? <p className="text-[12px] text-neutral-400">Aucun actif retenu.</p>
            : Object.entries(parType).map(([type, montant]) => (
              <div key={type} className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-0">
                <span className="text-[12px] text-neutral-600">{type}</span>
                <span className="text-[13px] font-medium text-neutral-900">{euros(montant)}</span>
              </div>
            ))}
        </div>

        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <p className="text-[12px] font-medium text-neutral-700 mb-3">
            Estimation brute par mécanisme
          </p>
          {Object.keys(parMecanisme).length === 0
            ? <p className="text-[12px] text-neutral-400">Aucun mécanisme valorisé.</p>
            : Object.entries(parMecanisme).map(([meca, montant]) => (
              <div key={meca} className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-0">
                <span className="text-[12px] text-neutral-600">
                  {MECANISMES_LIBELLES[meca] || meca}
                </span>
                <span className="text-[13px] font-medium text-neutral-900">{euros(montant)}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Les écartés, NOMMÉS par leur motif. « 12 actifs écartés » laisse chercher ;
          « 8 sans mandat, 4 déconnectés » dit quel geste répare quoi. */}
      {Object.keys(exclusions).length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-5" data-testid="agg-exclusions">
          <p className="text-[12px] font-medium text-neutral-700 mb-3">
            Actifs écartés du calcul, et pourquoi
          </p>
          {Object.entries(exclusions).map(([motif, info]) => (
            <div key={motif} className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-0">
              <span className="text-[12px] text-neutral-600">{info.libelle || motif}</span>
              <span className="text-[13px] font-medium text-neutral-500">
                {info.nombre} actif{info.nombre > 1 ? 's' : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {isAdmin && (
        <div className="rounded-xl border border-neutral-200 bg-white p-5 space-y-4" data-testid="agg-reglages">
          <div>
            <p className="text-[12px] font-medium text-neutral-700">Réglages du calcul</p>
            <p className="text-[11px] text-neutral-500">
              Ils s&apos;appliquent APRÈS le barème, et jamais dedans : le moteur reste
              celui de Symphonics et Survoltage.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] text-neutral-500">
                État du parc (0 à 1) — part des actifs réellement joignables
              </Label>
              <Input type="number" step="0.01" min="0" max="1" className="h-9"
                     value={reglages.etat_parc}
                     onChange={(e) => setReglages(r => ({ ...r, etat_parc: e.target.value }))}
                     data-testid="agg-etat-parc" />
            </div>
            <div>
              <Label className="text-[11px] text-neutral-500">
                Part opérateur (0 à 1)
              </Label>
              <Input type="number" step="0.01" min="0" max="1" className="h-9"
                     value={reglages.part_operateur}
                     onChange={(e) => setReglages(r => ({ ...r, part_operateur: e.target.value }))}
                     data-testid="agg-part-operateur" />
            </div>
          </div>
          <p className="text-[11px] text-neutral-400">
            Le barème lui-même — montants par type d&apos;actif et par mécanisme — se
            modifie depuis l&apos;écran Agrégateurs, bouton « Modifier ».
          </p>
        </div>
      )}
    </div>
  );
}
